
import React, { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from "react-error-boundary";
import { useApiData } from '../../contexts/apiService';
import { ProducerDefinitionItem } from '../../contexts/tableDefinitions';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import Dropdown, { DropdownOption } from '../../components/common/dropdown/dropdown';
import { convertDateTimeForm, getCurrentDateTimeLocal, isArrayEmpty } from './helperfunctions';


interface PoliciesProps {
    hasAdminAccess: boolean;
    activeUser: string;
}

interface CarrierContact {
    name: string;
    id: string;
}

interface userItem {
    name: string;
    id: string;
    company: string;
    email: string;
}

interface ProducerContact {
    name: string;
    id: string;
}

interface dropdownItem {
    data: unknown[];
}

interface IncidentRowData {
    [key: string]: any;
}

const Audits: React.FC<PoliciesProps> = ({ activeUser, hasAdminAccess }) => {
    const { apiData, isLoading, handleAddRow, handleDeleteRow, handleEditOrder } = useApiData();
    const [carrierFilters, setCarrierFilters] = useState<CarrierContact | []>([]);
    const [allRockbridgeUsers, setAllRockbridgeUsers] = useState<userItem[]>([]);

    const carrierDataRef = React.useRef<any[]>([]);
    const carrierData = useMemo(() => {
        if (true) {
            const value = apiData?.carrier_definition || [];
            carrierDataRef.current = value;
            return value;
        }
        return carrierDataRef.current;
    }, [apiData]);

    const auditDataRef = React.useRef<any[]>([]);
    const auditData = useMemo(() => {
        if (true) {
            const value = apiData?.audit_table || [];
            auditDataRef.current = value;
            return value;
        }
        return auditDataRef.current;
    }, [apiData]);

    const incidentsDefinitionRef = React.useRef<any[]>([]);
    const incidentsDefinition = useMemo(() => {
        if (true) {
            const value = apiData?.incidents_definition || [];
            incidentsDefinitionRef.current = value;
            return value;
        }
        return incidentsDefinitionRef.current;
    }, [apiData]);

    const producerDataRef = React.useRef<any[]>([]);
    const producerData = useMemo(() => {
        if (true) {
            const value = apiData?.producer_definition || [];
            producerDataRef.current = value;
            return value;
        }
        return producerDataRef.current;
    }, [apiData]);

    useEffect(() => {
        async function fetchRockbridgeUsers() {
            const db = getFirestore();
            const usersCollection = collection(db, "users");
            const snapshot = await getDocs(usersCollection);
            const usersList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }) as userItem);
            const rockbridgeUsers = usersList.filter(
                user => user.company && user.company.toLowerCase() === "rockbridge"
            );
            const mappedRockbridgeUsers = rockbridgeUsers.map(user => ({
                email: user.email,
                name: user.name,
                id: user.id
            }) as userItem);
            setAllRockbridgeUsers(mappedRockbridgeUsers);
        }
        fetchRockbridgeUsers();
    }, []);

    const CarriersDropdown = (data: dropdownItem) => {
        let options: DropdownOption[] = [];
        if (!data) return options;
        for (const item of data.data as userItem[]) {
            options.push({
                value: item.id,
                label: item.name
            });
        }
        options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        return options;
    };

    const ProducersDropdown = (data: dropdownItem) => {
        let options: DropdownOption[] = [];
        if (!data) return options;
        let alreadyEntered = false;
        for (const item of data.data as ProducerDefinitionItem[]) {
            alreadyEntered = options.some(option => option.label === item.producer_name);
            if (!alreadyEntered) {
                options.push({
                    value: String(item.id),
                    label: item.producer_name
                });
            }
        }
        options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        return options;
    };
    /**
     * Handles the selection of a new carrier from a dropdown component.
     * Finds the carrier in `carrierData` matching the provided `selectedOption` ID,
     * constructs a `CarrierContact` object, and updates the carrier filters state.
     *
     * @param selectedOption - The ID of the selected carrier option.
     */

    const newCarrierSelected = (selectedOption: number) => {
        if (selectedOption >= 0) {
            const temp = carrierData.find((item) => item.id == selectedOption);
            let selectedCarrier: CarrierContact;
            selectedCarrier = temp ? { name: temp.name, id: String(temp.id) } : { name: '', id: '' };
            setCarrierFilters(selectedCarrier ? selectedCarrier : []);
        } else {
            setCarrierFilters([]);
        }
    };

    // Delete handler for audit entries

    async function handleDelete(auditNumber: string | number) {
        if (!auditNumber) {
            alert('No auditNumber provided.');
            return;
        }
        if (window.confirm(`Are you sure you want to delete audit #${auditNumber}?`)) {
            alert(`Audit #${auditNumber} scheduled for deletion.`);
            await handleDeleteRow(new Set([String(auditNumber)]), "audit_table");
            window.location.reload();
        }
    }

    //old spot of carriersDropdown


    /**
     * 
     * This function filters the `auditData` array based on the selected carrier's ID,
     * if `carrierFilters` is provided and contains an `id` property. It logs the audit data
     * before filtering and the filtered result after applying the filter.
     *
     * @remarks
     * - If `carrierFilters` is not provided or does not contain an `id`, the original `auditData` is used.
     * - As the carrierFilers is set to an empty array by default, the function will not return null, so if the id
     * is not found, it will return the original `auditData`.
     * - The function is intended for debugging and inspection purposes.
     */

    function formatHeader(key: string): string {
        switch (key) {
            case 'auditDate':
                return 'Audit Date';
            case 'auditNumber':
                return 'Audit ID';
            case 'auditLocation':
                return 'Audit Location';
            case 'carrierID':
                return 'Carrier ID';
            case 'driverID':
                return 'Driver ID';
            case 'name':
                return 'Carrier Name';
            case 'producer':
                return 'Producer Name';
            default:
                return key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
        }
    }
    /**
     * Renders a filtered and formatted table of audit data based on the selected carrier filter.
     *
     * - Filters `auditData` by `carrierFilters` if provided, matching by carrier ID.
     * - Formats the `auditDate` field to a localized date string.
     * - Selects the fields to be displayed and corrects empty or missing values for requested fields by displaying 'N/A'.
     * - If no data is available after filtering, displays a message indicating no audit data is available.
     * - Otherwise, renders a table with dynamically generated headers and rows based on the filtered and formatted data.
     *
     * @returns {JSX.Element} A table displaying the filtered audit data, or a message if no data is available.
     */

    function RowDetailsModal({ rowData, onClose }: { rowData: IncidentRowData; onClose: () => void }) {
        const [editMode, setEditMode] = useState<boolean>(false);
        const [inProgress, setInProgress] = useState<boolean>(false);
        const getDisplayValue = (key: string, value: any) => {
            if (value === null || value === undefined) return 'N/A';
            
            switch (key) {
                case 'auditDate':
                    return new Date(value).toLocaleDateString('en-US');
                case 'carrierID':
                    const carrier = carrierData.find((item) => String(item.id) === String(value));
                    return carrier ? carrier.name : 'N/A';
                case 'incidentClassification':
                    const classification = incidentsDefinition.find((item) => String(item.incidentsClassification) === String(value));
                    return classification ? classification.description : 'N/A';
                case 'auditPoints':
                    return value >= 0 ? value : 'N/A';
                case 'driverID':
                    return `Firebase Placeholder: ${value}`;
                default:
                    return String(value);
            }
        };

        const [form, setForm] = useState({
            date: convertDateTimeForm(rowData.auditDate),
            name: rowData.name,
            carrier_number: rowData.carrierID,
            carrier_name: '',
            auditPoints: '',
            summary: rowData.summary || '',
            actions: rowData.remedialActions || '',
        });

        const record = useMemo(() => {
            return {
                auditDate: form.date,
                carrierID: form.carrier_number,
                driverID: '-1',
                auditLocation: '-1',
                producer: '',
                investigation: '-1',
                summary: form.summary,
                remedialActions: form.actions,
            };
        }, [form]);

    const [carrierSelection, setCarrierSelection] = useState<CarrierContact | []>({ name: rowData.name, id: rowData.carrierID });
        

        const newCarrierSelected = (selectedOption: number) => {
            if (selectedOption >= 0) {
                const temp = carrierData.find((item) => item.id == selectedOption);
                let selectedCarrier: CarrierContact;
                selectedCarrier = temp ? { name: temp.name, id: String(temp.id) } : { name: '', id: '' };
                setCarrierSelection(selectedCarrier ? selectedCarrier : []);
                setForm(prev => ({ ...prev, carrier_number: selectedCarrier.id, carrier_name: selectedCarrier.name }));
            } else {
                setCarrierSelection([]);
            }
        };


        const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value } = e.target;
            setForm(prev => ({ ...prev, [name]: value }));
        };

        useEffect(() => {
            const hasInput = Object.values(form).some(val => val && val.trim() !== '');
            setInProgress(hasInput);
        }, [form]);
        

        const [ProducerSelected, setProducerSelection] = useState<ProducerContact | null>(
            rowData.producer
                ? (() => {
                    const producer = producerData.find((item) => String(item.id) === String(rowData.producer));
                    return producer ? { name: producer.producer_name, id: String(producer.id) } : null;
                })()
                : null
        );
        //console.log("rowData.producerID:", ProducerSelected);

        const newProducerSelected = (selectedOption: number) => {
            var filteredData = ProducersDropdown({ data: producerData });
            if (selectedOption >= 0) {
                var temp = null;
                var selectedProducer: CarrierContact;
                try {
                    temp = filteredData.find((item) => Number(item.value) == selectedOption);
                    selectedProducer = temp ? { name: temp.label, id: String(temp.value) } : { name: '', id: '' };
                    setProducerSelection(selectedProducer ? selectedProducer : null);
                } catch (error) {
                    console.error(error);
                }
            } else {
                setProducerSelection(null);
            }
        };

        const getDisplayLabel = (key: string) => {
            return formatHeader(key);
        };

        const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (form.date && form.carrier_number) {
                var fullsubmit;
                fullsubmit = Object.entries(form).map(([key, value]) => ({ key, value }));
                const newRecord = {
                    auditDate: form.date,
                    carrierID: form.carrier_number,
                    driverID: '-1',
                    auditLocation: '-1',
                    producer: ProducerSelected ? ProducerSelected.id : '',
                    investigation: '-1',
                    summary: form.summary,
                    remedialActions: form.actions,
                };
                window.alert("Submitting changes to the audit table");
                await Promise.all(Object.entries(newRecord).map(async ([key, newValue]) => {
                    const oldValue = (record as any)[key];
                    if (oldValue !== newValue) {
                        console.log("Submitting change for key:", key, "from", oldValue, "to", newValue);
                        await handleEditOrder("audit_table", rowData.auditNumber, key, oldValue, newValue);
                    }
                }));
            } else {
                console.error("All fields are required");
            }
        };
        return (
            <div className='carrier-modal-overlay' onClick={onClose}>
                {!editMode? 
                    <div className='carrier-modal-content' onClick={e => e.stopPropagation()}>
                        <div className='carrier-modal-header'>
                            <h2>Incident Details</h2>
                            <button onClick={onClose} className="carrier-modal-close-btn">
                                <span className="carrier-close">&times;</span>
                            </button>
                        </div>

                        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {Object.entries(rowData).map(([key, value]) => (
                                <div key={key} className="incident-details-item">
                                    <strong className="incident-details-label">
                                        {getDisplayLabel(key)}:
                                    </strong>
                                    <div
                                        className={
                                        "incident-details-value" +
                                        ((key === 'incidentSummary' || key === 'remedialActions') ? " prewrap" : "")
                                        }
                                    >
                                        {getDisplayValue(key, value)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="incident-details-footer">
                            <button onClick={onClose} className="carrier-modal-submit-btn">
                                Close
                            </button>
                            <button onClick={()=>setEditMode(true)} className="carrier-modal-submit-btn edit">
                                Edit
                            </button>
            
                            <button
                                className="carrier-modal-submit-btn delete"
                                onClick={() => handleDelete(rowData.auditNumber)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                //display the edit modal if editMode is true vvvvvv
                :
                    <div className='carrier-modal-content' onClick={e => e.stopPropagation()}>
                        <div className='carrier-modal-header'>
                            <h2>Edit Audit Entry</h2>

                            <form className="carrier-modal-form" onSubmit={(e) => {handleSubmit(e)}} >
                                <div>
                                    <label className="carrier-modal-form-label" htmlFor="date">Date: </label>
                                    <input
                                    type="datetime-local"
                                    className='carrier-modal-form-input'
                                    id="name"
                                    name="date"
                                    value={form.date}
                                    max={getCurrentDateTimeLocal()}
                                    onChange={handleChange}
                                    required
                                    />
                                </div>    
                                
                                <div>
                                    <label className="carrier-modal-form-label" htmlFor="location">Carrier: </label>
                                        <Dropdown
                                            options={CarriersDropdown({ data: carrierData })}
                                            placeholder={typeof carrierSelection === 'object' && 'name' in carrierSelection ? carrierSelection.name : "Select Carrier"}
                                            onChange={(value) => {
                                                newCarrierSelected(Number(value));
                                            }}
                                        />
                                        {typeof carrierSelection === 'object' && 'id' in carrierSelection ? (
                                            <>
                                                CarrierID: {carrierSelection && carrierSelection.id}
                                            </>
                                        ) : ""}
                                        
                                </div>
                                <div>
                                    <label className="carrier-modal-form-label" htmlFor="Producer">Producer: </label>
                                    <Dropdown
                                        options={ProducersDropdown({ data: producerData })}
                                        placeholder={ProducerSelected? ProducerSelected.name : "Select Producer"}
                                        onChange={(value) => {
                                            newProducerSelected(Number(value));
                                        }}
                                    />
                                    {isArrayEmpty(ProducerSelected)?(
                                        <>
                                            ProducerID: {ProducerSelected?.id}
                                        </> 
                                    ):""}  
                                </div>
                                <div>
                                    <label className="carrier-modal-form-label" htmlFor="location">Driver: </label>
                                    
                                </div>
                                <div>
                                    <label className="carrier-modal-form-label" htmlFor="summary">Audit Summary: </label>
                                    <textarea
                                    className={`carrier-modal-form-input${form.summary !== record.summary ? ' field-changed' : ''}`}
                                    id="summary"
                                    name="summary"
                                    value={form.summary}
                                    onChange={handleChange}
                                    required
                                    />
                                </div>
                                <div>
                                    <label className="carrier-modal-form-label" htmlFor="actions">Remedial Actions: </label>
                                    <textarea
                                    className={`carrier-modal-form-input${form.actions !== record.remedialActions ? ' field-changed' : ''}`}
                                    id="actions"
                                    name="actions"
                                    value={form.actions}
                                    onChange={handleChange}
                                    
                                    />
                                </div>
                                

                                <button className = "carrier-modal-submit-btn" type="submit">Submit Changes</button>
                            </form>
                            <button onClick={onClose} className="carrier-modal-close-btn">
                                <span className="carrier-close">&times;</span>
                            </button>
                        </div>
                        
                    </div>
                }
            </div>
        );
    }


    function DisplayRowData() {
        //console.log("Audit Table Data BEFORE Filtering:", auditData);    
        const [selectedRowData, setSelectedRowData] = useState<IncidentRowData | null>(null);
        const temp = (carrierFilters? auditData.filter((item) => {
                
                if (typeof carrierFilters === 'object' && 'id' in carrierFilters) {
                    try{
                        return item['carrierID'] == Number(carrierFilters.id); // Filter by selected carrier ID
                    }catch (error){
                        console.error(error);
                        return item['carrierID'];
                    }
                }else{
                    return item['carrierID'];
                }
            }) : auditData);  
            //console.log("Audit Table Data AFTER Filtering:", temp);
        let resultArray: any[] = [];
        for(let i in temp){
            let tempObj: any = {};
            let originalData: any = {...auditData[i]};
            //console.log(temp[i]);
            for(let j in temp[i]){
                
                if(j === 'auditDate'){
                    tempObj[j] = new Date(temp[i][j]).toLocaleDateString('en-US');
                }else{
                    switch(j){
                        case 'name':
                        case 'producerID':
                        case 'lease':
                        case 'producer_name':
                        case 'investigation':
                            tempObj[j] = temp[i][j] || 'N/A'; // Handle empty
                            break; 
                        case 'driverID':
                            tempObj[j] = 'Firebase Placeholder: '+ temp[i][j]; // Handle empty
                            break;
                    }
                }
            }
            tempObj._originalData = originalData;
            resultArray.push(tempObj);
        }


        if (resultArray.length === 0) {
            return <div className="no-data-message">No audit data available for the selected carrier.</div>;
        }

        const handleRowClick = (item: any) => {
            // Pass the original data to the modal
            setSelectedRowData(item._originalData);
        };

        const closeModal = () => {
            setSelectedRowData(null);
        };

        return(
            <>
                {/* Only render modal when selectedRowData exists */}
                {selectedRowData && (
                    <RowDetailsModal 
                        rowData={selectedRowData} 
                        onClose={closeModal} 
                    />
                )}
                <table className="audit-table">
                    <tbody>
                        <tr>
                            {resultArray.length > 0 &&
                                Object.keys(resultArray[0])
                                    .filter(key => key !== '_originalData') // Don't show the original data column
                                    .map((key, idx) => (
                                        <th key={idx}>{formatHeader(key)}</th>
                                    ))
                            }
                        </tr>
                        {resultArray.map((item, idx) => (
                            <tr 
                                key={item.auditNumber || idx}
                                onClick={() => handleRowClick(item)}
                                style={{ cursor: 'pointer' }}
                                className="clickable-row"
                            >
                                {Object.entries(item)
                                    .filter(([key]) => key !== '_originalData') // Don't render the original data
                                    .map(([key, value], i) => (
                                        <td key={i}>{String(value)}</td>
                                    ))
                                }
                            </tr>
                        ))}
                    </tbody>
                </table>
            </>
            
        );   
    }
    
    
    const [auditModalShow, setAuditModalShow] = useState<boolean>(false);
    
    
    function createNewAuditEntry() {
        setAuditModalShow(true);
    }


    function AuditEntry() {
        const [inProgress, setInProgress] = useState<boolean>(false);
        const [ProducerSelected, setProducerSelection] = useState<ProducerContact|null>(null);
        const [form, setForm] = useState({
            date: '',
            name: activeUser,
            carrier_number:'',
            carrier_name:'',
            auditPoints: '',
            summary: '',
            actions: '',

        });
    const newProducerSelected = (selectedOption: number) => {
            var filteredData = ProducersDropdown({ data: producerData });
            if(selectedOption >=0){
                var temp = null;
                var selectedProducer: CarrierContact;
                try{
                    temp = filteredData.find((item) => Number(item.value) == selectedOption);
                    selectedProducer = temp ? { name: temp.label, id: String(temp.value) } : { name: '', id: '' };
                    setProducerSelection(selectedProducer ? selectedProducer : null);
                }catch(error){
                    console.error(error);
                }
            }else{
                setProducerSelection(null);
            }
        };
    const [carrierSelection, setCarrierSelection] = useState<CarrierContact | []>([]);
        
    const newCarrierSelected = (selectedOption: number) => {
            if(selectedOption >=0){
                const temp = carrierData.find((item) => item.id == selectedOption);
                let selectedCarrier: CarrierContact ;
                selectedCarrier = temp ? { name: temp.name, id: String(temp.id) } : { name: '', id: '' };
                setCarrierSelection(selectedCarrier ? selectedCarrier : []);
                setForm(prev => ({ ...prev, carrier_number: selectedCarrier.id, carrier_name:selectedCarrier.name }));
            }else{
                setCarrierSelection([]);
            }
        };

        // Update form state on input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value } = e.target;
            setForm(prev => ({ ...prev, [name]: value }));
        };


        // Watch for changes and set inProgress accordingly
    useEffect(() => {
            const hasInput = Object.values(form).some(val => val && val.trim() !== '');
            setInProgress(hasInput);
        }, [form]);

        
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        //institute form verification here in the future, for now just makes sure that Name, email, and position are included, phone not required
        e.preventDefault();
        if(form.date && form.carrier_number){
            var  fullsubmit
            fullsubmit = Object.entries(form).map(([key, value]) => ({ key, value }));
            const record = {
                auditDate: form.date,
                carrierID: form.carrier_number,
                driverID: '-1', 
                auditLocation: '-1',
                producer: ProducerSelected ? ProducerSelected.id : '',
                investigation: '-1',
                summary: form.summary,
                remedialActions: form.actions,
            };
            
            await handleAddRow("audit_table", record);
            alert("Form submitted successfully!");
            //handleNewUser(formData.name, formData.email, formData.position, formData.phone);
            setForm({
                date: '',
                name: activeUser,
                carrier_number:'',
                carrier_name:'',
                auditPoints: '',
                summary: '',
                actions: '',
            });
            
            window.location.reload();
            
        } else{
            console.error("All fields are required");
        }
        };

        const isArrayEmpty = (array_1: CarrierContact | null) => {
            return array_1 ? Object.keys(array_1).length : 0;
        };


    return (
            <div onClick={()=>{
                if(!inProgress){
                    setAuditModalShow(false);
                }else{
                    console.error("Form In Progress")
                }
            }} className='carrier-modal-overlay'>
                <div className='carrier-modal-content' onClick={e => e.stopPropagation()}>
                    Add Audit Report
                    <button onClick={()=>{setAuditModalShow(false)}} className="carrier-modal-close-btn">
                        <span className="carrier-close">&times;</span>
                    </button>
                    <form className="carrier-modal-form" onSubmit={(e) => {handleSubmit(e)}} >
                    <div>
                        <label className="carrier-modal-form-label" htmlFor="date">Date: </label>
                        <input
                        type="datetime-local"
                        className='carrier-modal-form-input'
                        id="date"
                        name="date"
                        value={form.date}
                        max={getCurrentDateTimeLocal()}
                        onChange={handleChange}
                        required
                        />
                    </div>    
                    
                    <div>
                        <label className="carrier-modal-form-label" htmlFor="location">Carrier: </label>
                            <Dropdown
                                options={CarriersDropdown({ data: carrierData })}
                                placeholder={typeof carrierSelection === 'object' && 'name' in carrierSelection ? carrierSelection.name : "Select Carrier"}
                                onChange={(value) => {
                                    newCarrierSelected(Number(value));
                                }}
                            />
                            {typeof carrierSelection === 'object' && 'id' in carrierSelection ? (
                                <>
                                    CarrierID: {carrierSelection && carrierSelection.id}
                                </>
                            ) : ""}
                            
                    </div>
                        <div>
                            <label className="carrier-modal-form-label" htmlFor="Producer">Producer: </label>
                            <Dropdown
                                options={ProducersDropdown({ data: producerData })}
                                placeholder={ProducerSelected? ProducerSelected.name : "Select Producer"}
                                onChange={(value) => {
                                    newProducerSelected(Number(value));
                                }}
                            />
                            {isArrayEmpty(ProducerSelected)?(
                                <>
                                    ProducerID: {ProducerSelected?.id}
                                </> 
                            ):""}  
                        </div>
                    <div>
                        <label className="carrier-modal-form-label" htmlFor="location">Driver: to be implemented</label>
                        
                    </div>
                    <div>
                        <label className="carrier-modal-form-label" htmlFor="summary">Audit Summary: </label>
                        <textarea
                        className='carrier-modal-form-input'
                        id="summary"
                        name="summary"
                        value={form.summary}
                        onChange={handleChange}
                        required
                        />
                    </div>
                    <div>
                        <label className="carrier-modal-form-label" htmlFor="actions">Remedial Actions: </label>
                        <textarea
                        className='carrier-modal-form-input'
                        id="actions"
                        name="actions"
                        value={form.actions}
                        onChange={handleChange}
                        
                        />
                    </div>
                    

                    <button className = "carrier-modal-submit-btn" type="submit">Submit</button>
                    </form>
                </div>
            </div>
        );
    }



    return (
        <div className="audit-table-container">
            
            <ErrorBoundary fallback={<div>Error loading audit data.</div>}></ErrorBoundary>
            
            <>
                <h1 className="audit-table-title">
                    Audits
                </h1>
                { auditModalShow ? (
                    <>
                        <AuditEntry />
                    </> 
                ): null}
            </>
            
            
            {isLoading? (
                <div className="loading-spinner">
                    <div className="spinner"></div>
                </div>
            ) : (
                <div>
                    
                    <table>
                        
                            <thead>
                                <tr>
                                    <th>
                                        <Dropdown
                                        options={CarriersDropdown({data: carrierData})}
                                        placeholder={typeof carrierFilters === 'object' && 'name' in carrierFilters ? carrierFilters.name : "Select Carrier"}
                                        
                                        onChange={(value)=>{
                                            try{
                                                //using try/catch due to typecasting
                                                newCarrierSelected(Number(value));
                                            }catch(error){
                                                console.error(error);
                                            }
                                        }}
                                        />
                                    </th>
                                    
                                    <th>
                                        { typeof carrierFilters === 'object' && 'id' in carrierFilters && carrierFilters.id != null ? (
                                            <button onClick={()=>newCarrierSelected(-1)}> 
                                        Clear Carrier
                                        </button>
                                        )
                                        :null}                                        
                                    </th>
                                    <th>
                                        <button className='addAuditEntry' onClick={()=>{setAuditModalShow(!auditModalShow)}}>
                                            +
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                                    
                    </table>
                    
                    <DisplayRowData />
                </div>
            )}
            

        </div>
    );
}
export default Audits;